import { Component, OnInit } from '@angular/core';
import { RecordService } from '../../../../../services/record.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Form, FormMode } from '../../../../../base/form';
import { LooseObject } from '../../../../../objects/loose-object';
import moment from 'moment';
import { filled } from '../../../../../shared/utils/common';
import { cloneDeep, get } from 'lodash-es';
import { filter, switchMap, tap } from 'rxjs/operators';
import { Relate } from '../../../../../objects/relate';
import { Select } from '../../../../../objects/select';
import { ViewService } from '../../../../../services/view.service';
import { NotificationService } from '../../../../../services/notification.service';

@Component({
  selector: 'app-user-skills',
  templateUrl: './user-skills.component.html',
  styleUrls: ['./user-skills.component.scss'],
})
export class UserSkillsComponent implements OnInit {

  strMode: string = '';

  objParentForm: FormGroup;

  objMetadata: LooseObject = {
    skill_id: {
      key: 'skill_id',
      controlType: 'relate',
      default_value: '',
      required: true,
      module: 'skills'
    },
    expiry_date: {
      key: 'expiry_date',
      controlType: 'date',
      required: true,
      default_value: '',
      max_length: 128,
    },
    note: {
      key: 'note',
      controlType: 'textarea',
      required: false,
      default_value: '',
      rows: 2,
      max_length: 10000
    },
  }

  objForm: FormGroup = new FormGroup({
    skill_id: new FormControl(null),
    expiry_date: new FormControl(null),
    note: new FormControl(null),
  });

  arFormLineItems: LooseObject[] = [];

  arDeletedLineItems: string[] = [];

  arLineItems: LooseObject[] = [];

  get isEdit(): boolean {
    return this.strMode === 'edit';
  }

  get currentUserId(): string {
    const objRecord = this.recordService.getParentData();

    return get(objRecord, 'id', '');
  }

  constructor(
    private recordService: RecordService,
    private notificationService: NotificationService,
  ) {

  }

  ngOnInit() {
  }

  /**
   * @inheritdoc
   */
  setFormMode(mode: FormMode): void {
    this.strMode = mode;
  }

  /**
   * @inheritdoc
   */
  setParentForm(parentForm: FormGroup): void {
    this.objParentForm = parentForm;
    if (filled(get(parentForm.value, 'user_skills'))) {
      parentForm.value.user_skills.forEach( data => {
        this.addLineItem(data);
      });
    } else {
      this.addLineItem();
    }
  }

  /**
   * @inheritdoc
   */
  setField(field: Form<any>): void {
    this.arLineItems = get(field, 'default_value');
  }

  /**
   * add skill line item
   *
   * @param defaultValue
   */
  addLineItem(defaultValue: LooseObject = {}): void {

    let objMetadata = cloneDeep(this.objMetadata);
    let objSkillDefaultValue = [];
    if (
      get(defaultValue, 'skill_id')
      && get(defaultValue, 'skill_text')
    ) {
      objSkillDefaultValue = [ new Select(defaultValue.skill_id, defaultValue.skill_text) ];
    }

    let strExpiryDate = null;
    if (get(defaultValue, 'expiry_date')) {
      strExpiryDate = moment(defaultValue.expiry_date).format('YYYY-MM-DD');
      objMetadata.expiry_date.default_value = strExpiryDate;
    }

    let objFormGroup = new FormGroup({
      id: new FormControl(get(defaultValue, 'id')),
      skill_id: new FormControl(get(defaultValue, 'skill_id'), Validators.required),
      expiry_date: new FormControl(strExpiryDate, Validators.required),
      note: new FormControl(get(defaultValue, 'note')),
    });

    this.arFormLineItems.push({
      form: objFormGroup,
      skill_relate: (new Relate<Select[]>()).buildRelates(
        switchMap(strTerm => this.recordService.getRecordRelate(
            'skills',
            strTerm,
            false,
            false,
          ).pipe(
            tap( (response) => {
              let arSkillIds = this._getSelectedSkill();
              return response.map( skill => {
                skill.disabled = arSkillIds.includes(skill.id);
                return skill
              })
            })
          )
        ),
        objSkillDefaultValue,
        filled(objSkillDefaultValue)
      ),
      metadata: objMetadata
    });

    objFormGroup.valueChanges.subscribe( data => {
      this._syncRecord();
    })
  }

  /**
   * remove selected line item
   *
   * @param index
   */
  removeLineItem(index: number): void {
    if (filled(this.arFormLineItems[index])) {

      let strSkillId = this.arFormLineItems[index].form.controls['id'].value;
      if (strSkillId) {
        this.arDeletedLineItems.push(strSkillId);
      }

      this.arFormLineItems.splice(index, 1);
      this._syncRecord();
    }
  }

  /**
   * update the line item form when changing skill
   *
   * @param objLineItem
   * @param index
   */
  onChangeSkill(objLineItem: LooseObject, index: number) {
    let objExpiryDate = moment().local();
    let arSkillIds = this._getSelectedSkill();
    let objSelectedSkill = objLineItem.skill_relate.value;
    if (arSkillIds.includes(objSelectedSkill.id) == false) {

      const [ intQty, strFrequency ] = get(objSelectedSkill, 'renewal', '').split(' ');
      if (filled(intQty) && filled(strFrequency)) {
        objExpiryDate.add(
          parseInt(intQty),
          (strFrequency == 'mon' || strFrequency == 'mons') ? 'months' : strFrequency
        )
      }

      objLineItem.form.patchValue({
        skill_id: get(objSelectedSkill, 'id', null),
        expiry_date: objExpiryDate.format('YYYY-MM-DD')
      });
    } else {
      if (filled(objSelectedSkill.id)) {
        this.notificationService.notifyWarning('already_selected');
        objLineItem.skill_relate.value = [];
        objLineItem.form.patchValue({
          skill_id: null,
          expiry_date: null,
        });
      }
    }
  }

  /**
   * sync to update record to parent form
   */
  private _syncRecord(): void {
    if (this.objParentForm) {
      this.objParentForm.controls.user_skills.setValue(
        {
          upsert: this.arFormLineItems
            .map( lineItem => lineItem.form.getRawValue() )
            .filter( lineItem => filled(lineItem.skill_id) && filled(lineItem.expiry_date) ),
          delete: this.arDeletedLineItems
        }
      );
    }
  }

  private _getSelectedSkill(): string[] {
    return this.arFormLineItems
      .map( lineItem => lineItem.form.controls['skill_id'].value )
      .filter( skillId => skillId );
  }
}
